import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

/** What a bar's width becomes when collapsed to its rail. */
export const COLLAPSED_BAR_WIDTH_CLASS = "w-12";

interface SidebarHeaderProps {
  /** The bar's title, shown upright here and rotated on its rail. */
  title: string;
  /** Accessible name of the collapse button, e.g. "Hide plans". */
  collapseLabel: string;
  onCollapse: () => void;
  /** The id of the content the button folds away, for `aria-controls`. */
  controlsId?: string;
}

/**
 * The title row of an expanded bar: its title and a collapse button. Shared by
 * the plan viewer's plans bar and filter bar so the two look alike.
 * See [[collapsible-sidebar.spec]].
 */
export function SidebarHeader({
  title,
  collapseLabel,
  onCollapse,
  controlsId,
}: SidebarHeaderProps) {
  return (
    <div className="flex h-11 shrink-0 items-center justify-between border-b border-border pl-4 pr-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 text-muted-foreground"
        aria-label={collapseLabel}
        aria-expanded
        aria-controls={controlsId}
        title={collapseLabel}
        onClick={onCollapse}
      >
        <PanelLeftClose className="size-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

interface SidebarRailProps {
  /** The bar's title, turned on its side so a collapsed bar still says what it is. */
  title: string;
  /** Accessible name of the whole rail, e.g. "Show plans". */
  expandLabel: string;
  onExpand: () => void;
  /** A count to badge on the icon (active filters); nothing is drawn for 0. */
  badge?: number;
}

/**
 * What a bar collapses to: an expand icon over the bar's title rotated to run
 * vertically (bottom to top). The whole rail is the expand button, so any part
 * of the strip works. See [[collapsible-sidebar.spec]].
 */
export function SidebarRail({
  title,
  expandLabel,
  onExpand,
  badge,
}: SidebarRailProps) {
  return (
    <button
      type="button"
      data-slot="sidebar-rail"
      aria-expanded={false}
      aria-label={badge ? `${expandLabel} (${badge} active)` : expandLabel}
      title={expandLabel}
      onClick={onExpand}
      className="flex h-full w-full cursor-pointer flex-col items-center gap-3 overflow-hidden py-3 text-muted-foreground outline-none transition-colors hover:bg-accent/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <span className="relative shrink-0">
        <PanelLeftOpen className="size-4" aria-hidden="true" />
        {!!badge && (
          <span
            aria-hidden="true"
            className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-tertiary px-1 text-[9px] font-semibold text-tertiary-foreground"
          >
            {badge}
          </span>
        )}
      </span>
      {/* Decorative: the button's label already names the bar. `rotate-180` on
          a top-to-bottom vertical run reads bottom to top, like a tab on a
          left-hand edge. */}
      <span
        aria-hidden="true"
        className="select-none text-xs font-semibold uppercase tracking-wider [writing-mode:vertical-rl] rotate-180"
      >
        {title}
      </span>
    </button>
  );
}
